import { ULM_CLIENTS } from "@constants/ulm-constants";
import { QueryParams } from "@router/route-params";
import { EAppRoutes } from "@router/router-helper";
import { getQueryParamsFromSearch } from "@utils/url-utils";

/**
 * Defines the structure of the user module redirect route.
 */
export interface UserModuleRedirectRoute {
  /** The route in the Entry Page that should be redirected to. E.g. Login */
  route: EAppRoutes;

  /**
   * The original route in the user module that triggered the redirect. E.g. login.
   * Currently unused but will be helpful when we integrate Amplitude in the EntryPage
   */
  originalRoute: string;

  /**
   * The original query parameters from the user module that triggered the redirect. E.g. [redirectUrl, client]
   * Currently unused but will be helpful when we integrate Amplitude in the EntryPage
   */
  originalQueryParams: string[];

  /**
   * Additional query parameters to include in the redirect that were not originally included or that the name changed
   * E.g. for redirection from the JWA we add the client jwahb
   */
  additionalQuery?: Record<string, string>;

  /**
   * Flag whether an error should be triggered in Sentry due to an incorrect redirect.
   */
  shouldShowError?: boolean;
}

/**
 * Defines the structure of the user module redirect route.
 * The hash is the route coming from the user-module
 * The route is the corresponding route in the Entry Page.
 * Some routes like the login require special handling, therefore are handled separately.
 */
const REDIRECT_ROUTES = [
  {
    hash: "#login",
    route: EAppRoutes.Login,
  },
  {
    hash: "#loading",
    route: EAppRoutes.Login,
  },
  {
    hash: "#userinfo",
    route: EAppRoutes.Login,
  },
  {
    hash: "#useotherbrowser",
    route: EAppRoutes.Login,
  },
  {
    hash: "#resendVerification",
    route: EAppRoutes.ResendEmail,
  },
  {
    hash: "#logout",
    route: EAppRoutes.Logout,
  },
  {
    hash: "#pwrecovery",
    route: EAppRoutes.PasswordRecovery,
  },
  {
    hash: "#pwreset",
    route: EAppRoutes.ResetPassword,
  },
  {
    hash: "#verifymail",
    route: EAppRoutes.VerifyEmail,
  },
  {
    hash: "#ssologin",
    route: EAppRoutes.SsoLogin,
  },
  {
    hash: "#register",
    route: EAppRoutes.SignupTrial,
  },
];

/**
 * Gets a valid route in the EntryPage and query parameters
 * from the user module's URL hash.
 * This is used when the user is redirected from the user module and the original url is kept.
 */
export function getUlmRouteFromUserModule({
  hash,
}: {
  hash: string;
}): UserModuleRedirectRoute {
  if (!hash) {
    return {
      route: EAppRoutes.Login,
      originalRoute: "/",
      originalQueryParams: [],
    };
  }

  // Obtains the first part of the hash before query parameters and removes leading hash.
  // E.g. for #login?foo=bar we want login
  const originalRoute = hash.split("?")[0].replace("#", "");
  // Obtains an array with only the names of the query parameters
  // E.g. for #login?foo=bar&client=faro we want ["foo", "client"]
  const originalQueryParams = getQueryParamsFromSearch(
    hash.split("?")[1] || ""
  );

  // You can add additional query parameters depending on certain cases
  let additionalQuery: Record<string, string> | undefined;

  // If the route is coming from the JobWalk App we need to specify the client
  // and handle the login route different
  if (hash.includes("redirectUrl=jobwalk")) {
    additionalQuery = {
      [QueryParams.client]: ULM_CLIENTS.jwahb,
    };

    // For login to the JWA we don't use the normal login page but rather a custom provider login
    if (hash.includes("#login")) {
      return {
        route: EAppRoutes.ProviderLogin,
        originalQueryParams,
        originalRoute,
        additionalQuery,
      };
    }
  }

  if (hash.includes("#register")) {
    // Check if the original query params include company and email, but not createDashboard
    if (
      originalQueryParams.includes("company") &&
      originalQueryParams.includes("email") &&
      !originalQueryParams.includes("createDashboard")
    ) {
      return {
        route: EAppRoutes.SignupInvited,
        originalQueryParams,
        originalRoute,
        additionalQuery,
      };
    }
  }

  for (const redirectRoute of REDIRECT_ROUTES) {
    if (hash.includes(redirectRoute.hash)) {
      return {
        route: redirectRoute.route,
        originalQueryParams,
        originalRoute,
        additionalQuery,
      };
    }
  }

  // Returns with error since no valid mapping was found.
  // Still it is better to redirect to the login route instead that a critical error page.
  return {
    route: EAppRoutes.Login,
    originalRoute,
    originalQueryParams,
    shouldShowError: true,
  };
}
